// [ADM STORAGE GRAFICO] Component to display storage usage
import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
// [ADM STORAGE GRAFICO] Import formatação de bytes
import { formatBytesToHumanReadable } from '../utils/formatBytes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StorageChart = () => {
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStorageData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStorageData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStorageData = async () => {
    try {
      const response = await axios.get(`${API}/admin/storage/stats`);
      setStorageData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados de storage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#1a1a1a] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Uso de Storage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-400">Carregando...</p>
        </CardContent>
      </Card>
    );
  }

  if (!storageData) {
    return null;
  }

  const { percentualUsado, totalBytesUsados, storageMaxBytes, nomePlano } = storageData;
  
  // [ADM STORAGE GRAFICO] Formatar valores usando helper function
  const usadoFormatado = formatBytesToHumanReadable(totalBytesUsados);
  const maxFormatado = formatBytesToHumanReadable(storageMaxBytes);
  const livreFormatado = formatBytesToHumanReadable(storageMaxBytes - totalBytesUsados);
  
  // Determine color based on usage percentage
  const getColor = () => {
    if (percentualUsado >= 90) return '#ef4444'; // red
    if (percentualUsado >= 70) return '#f59e0b'; // orange
    return '#10b981'; // green
  };

  const barColor = getColor();

  return (
    <Card className="bg-[#1a1a1a] border-gray-800 hover:border-gray-700 transition-colors">
      <CardHeader>
        <CardTitle className="text-white text-xl">📊 Uso de Storage</CardTitle>
        <CardDescription className="text-gray-400">
          Plano: {nomePlano}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Text Summary */}
        <div className="text-center mb-4">
          <p className="text-2xl font-bold text-white">
            {usadoFormatado} <span className="text-gray-400">de</span> {maxFormatado}
          </p>
          <p className="text-lg text-gray-400 mt-1">
            ({percentualUsado.toFixed(1)}% usado)
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {livreFormatado} livres
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden">
          <div
            className="h-full flex items-center justify-center text-xs font-semibold text-white transition-all duration-500"
            style={{
              width: `${Math.min(percentualUsado, 100)}%`,
              backgroundColor: barColor
            }}
          >
            {percentualUsado > 5 && `${percentualUsado.toFixed(1)}%`}
          </div>
        </div>

        {/* Donut Chart (Simple CSS version) */}
        <div className="flex justify-center mt-6">
          <div 
            className="relative w-32 h-32 rounded-full flex items-center justify-center"
            style={{
              background: `conic-gradient(${barColor} 0% ${percentualUsado}%, #374151 ${percentualUsado}% 100%)`
            }}
          >
            <div className="absolute w-20 h-20 bg-[#1a1a1a] rounded-full flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{percentualUsado.toFixed(0)}%</p>
                <p className="text-xs text-gray-400">usado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Storage Warning */}
        {percentualUsado >= 90 && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 mt-4">
            <p className="text-red-400 text-sm font-medium">
              ⚠️ Atenção: Storage quase cheio! Considere fazer upgrade do plano.
            </p>
          </div>
        )}
        
        {percentualUsado >= 70 && percentualUsado < 90 && (
          <div className="bg-orange-900/20 border border-orange-700 rounded-lg p-3 mt-4">
            <p className="text-orange-400 text-sm font-medium">
              ⚡ Aviso: Storage acima de 70%. Monitore o uso.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StorageChart;
